import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { first, map } from 'lodash';
import { Observable, Subject, defer, merge, startWith, takeUntil } from 'rxjs';

@Component({
    selector: 'lui-selector',
    templateUrl: './selector.component.html',
    styleUrls: ['./selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SelectorComponent implements AfterContentInit, OnDestroy {
  @HostBinding('class') hostClass = 'lui-selector';

  @ContentChildren(MatOption)
  options!: QueryList<MatOption>;

  @ContentChild(MatFormField, { static: true })
  searchField!: MatFormField;

  @ContentChild(MatInput, { static: true })
  searchInput!: MatInput;

  @Input() get value() {
    return this._value;
  }
  set value(value: unknown) {
    this._value = value;
  }
  private _value: unknown;

  // @Output() selectionChange = new EventEmitter<
  @Output() valueChange = new EventEmitter<unknown>();

  destoryed$ = new Subject<void>();

  optionSelectionEvents$: Observable<MatOptionSelectionChange> = defer(() =>
    merge(
      ...map(this.options.toArray(), (option) =>
        option.onSelectionChange.asObservable()
      )
    )
  );

  private keyManager!: ActiveDescendantKeyManager<MatOption>;

  private selectionModel = new SelectionModel<MatOption>();

  protected _skipPredicate(item: MatOption): boolean {
    return item.disabled;
  }

  get empty(): boolean {
    return !this.selectionModel || this.selectionModel.isEmpty();
  }

  @Input() compareWith: (v1: unknown, v2: unknown) => boolean = (v1, v2) =>
    v1 === v2;

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    this.handleKeyDown(e);
  }

  constructor(private cd: ChangeDetectorRef, private renderer: Renderer2) {}

  ngAfterContentInit(): void {
    this.initKeyManager();

    this.searchField.appearance = 'fill';

    this.renderer.addClass(
      this.searchField._elementRef.nativeElement,
      'search-field'
    );

    this.selectionModel.changed
      .pipe(takeUntil(this.destoryed$))
      .subscribe((event) => {
        event.added.forEach((option) => {
          option.select();
        });
        event.removed.forEach((option) => option.deselect());

        this.propagateChanges();
      });

    this.options.changes
      .pipe(takeUntil(this.destoryed$), startWith(this.options))
      .subscribe(() => {
        const changedOrDestroyed$ = merge(
          this.options.changes,
          this.destoryed$
        );

        this.applyValueToSelection();
        this._highlightCorrectOption();

        this.optionSelectionEvents$
          .pipe(takeUntil(changedOrDestroyed$))
          .subscribe((e) => {
            this.onSelect(e.source, e.isUserInput);
          });
      });
  }

  ngOnDestroy(): void {
    this.destoryed$.next();
    this.destoryed$.complete();
  }

  applyValueToSelection() {
    const match = this.options.find((option) =>
      this.compareWith(option.value, this._value)
    );

    if (match) {
      this.selectionModel.select(match);
    }
  }

  private propagateChanges() {
    // const changedOrDestroyed$ = merge(this.options.changes, this.destoryed$);
    const values = map(
      this.selectionModel.selected,
      (selectOption) => selectOption.value
    );
    const valueToEmit = values.length > 1 ? values : first(values);

    this.valueChange.emit(valueToEmit);

    this.cd.markForCheck();
  }

  private onSelect(option: MatOption, isUserInput: boolean) {
    const wasSelected = this.selectionModel.isSelected(option);

    if (wasSelected !== option.selected) {
      option.selected
        ? this.selectionModel.select(option)
        : this.selectionModel.deselect(option);
    }

    if (isUserInput) {
      this.keyManager.setActiveItem(option);
    }
  }

  private initKeyManager() {
    this.keyManager = new ActiveDescendantKeyManager<MatOption>(this.options)
      .withTypeAhead(50)
      .withVerticalOrientation()
      .withHorizontalOrientation('ltr')
      .withHomeAndEnd()
      .withPageUpDown()
      .withAllowedModifierKeys(['shiftKey'])
      .skipPredicate(this._skipPredicate);
  }

  private handleKeyDown(e: KeyboardEvent) {
    const manager = this.keyManager;
    const keyCode = e.key;
    const isTyping = manager.isTyping();

    if (
      !isTyping &&
      ((!isTyping && keyCode === 'Enter') || keyCode === 'Space') &&
      manager.activeItem
    ) {
      e.preventDefault();
      manager.activeItem._selectViaInteraction();
    }

    manager.onKeydown(e);
  }

  /**
   * Highlights the selected item. If no option is selected, it will highlight
   * the first *enabled* option.
   */
  private _highlightCorrectOption(): void {
    if (this.keyManager) {
      if (this.empty) {
        // Find the index of the first *enabled* option. Avoid calling `_keyManager.setActiveItem`
        // because it activates the first option that passes the skip predicate, rather than the
        // first *enabled* option.
        let firstEnabledOptionIndex = -1;
        for (let index = 0; index < this.options.length; index++) {
          const option = this.options.get(index);
          if (option && !option.disabled) {
            firstEnabledOptionIndex = index;
            break;
          }
        }

        this.keyManager.setActiveItem(firstEnabledOptionIndex);
      } else {
        this.keyManager.setActiveItem(this.selectionModel.selected[0]);
      }
    }
  }
}
